import { Component, computed, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, take } from 'rxjs';
import { IntelligenceModels } from '@models/intelligence.model';
import { VehicleModels } from '@models/vehicle.model';
import { VehiclesActions } from '@states/vehicles/vehicles.action-types';
import { ConfirmDialogModel, ConfirmDialogService } from '@ui';
import { IntelligenceResultsService } from '../../../../../services/intelligence-results.service';
import { VehiclesTableComponent } from '../../../../../pages/database/pages/vehicles/components/vehicles-table/vehicles-table.component';

@Component({
  selector: 'app-vehicles-result',
  standalone: true,
  imports: [CommonModule, TranslatePipe, VehiclesTableComponent],
  templateUrl: './vehicles-result.component.html',
  styleUrls: ['./vehicles-result.component.scss'],
})
export class VehiclesResultComponent {
  private readonly resultsService = inject(IntelligenceResultsService);
  private readonly store = inject(Store);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  /** Read directly from the service — self-updates without any parent binding. */
  protected readonly response = computed<IntelligenceModels.Vehicles.Response | null>(() => {
    const result = this.resultsService.current();
    if (result?.type !== 'vehicles') return null;
    return result.data as IntelligenceModels.Vehicles.Response;
  });

  protected readonly vehicles = computed(() => this.response()?.vehicles ?? []);

  /**
   * Overrides the table's built-in delete flow: that flow keys its confirm
   * dialog off a row index on a global action, and every mounted table
   * instance subscribes to it — opening this panel on top of the vehicles
   * page would make both tables resolve the index against their own arrays
   * and double-dispatch.
   */
  protected removeVehicle = (vehicle: VehicleModels.Vehicle): void => {
    this.confirmDialog
      .open({
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: this.translate.instant('DELETE_VEHICLE_MODAL.TITLE'),
        msg: this.translate.instant('DELETE_VEHICLE_MODAL.MSG', { plate: vehicle.plate }),
        confirm: this.translate.instant('CONFIRM_MODALS.COMMON_YES'),
        cancel: this.translate.instant('COMMON.CANCEL'),
        disableClose: true,
      })
      .pipe(
        take(1),
        filter(
          (result) =>
            result?.selection === ConfirmDialogModel.ConfirmResult.OK &&
            result.type === ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() =>
        this.store.dispatch(VehiclesActions.removeVehiclesByPlates({ plates: [vehicle.plate] })),
      );
  };
}
